import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import { FiMail, FiCheckCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const ForgotUsername = () => {
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [otpArray, setOtpArray] = useState(["", "", "", "", "", ""]);
    const otpRefs = useRef([...Array(6)].map(() => React.createRef()));
    const navigate = useNavigate();

    // Sync otpArray to otp string for backend
    useEffect(() => {
        setOtp(otpArray.join(""));
    }, [otpArray]);

    // Add this effect to redirect after step 3
    useEffect(() => {
        if(step === 3) {
            const timer = setTimeout(() => {
                navigate("/login");
            }, 7000);
            return () => clearTimeout(timer);
        }
    }, [step, navigate]);

    const handleRequestOtp = async () => {
        if(!email) return notifyUser(null, "error", "Please enter your email", "toast", null, null, null, null, null, false, false, "top-center");

        setLoading(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/request-otp`,
                { email },
            );
            notifyUser(null, "success", res.data.message, "toast", null, null, null, null, null, false, false, "top-center");
            setStep(2);
        } catch(err) {
            notifyUser(null, "error", err.response?.data?.error || "Failed to send OTP", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if(!otp) return notifyUser(null, "error", "Please enter the OTP", "toast", null, null, null, null, null, false, false, "top-center");

        setLoading(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-otp`,
                { email, otp },
            );
            notifyUser(null, "success", res.data.message, "toast", null, null, null, null, null, false, false, "top-center");
            setStep(3);
        } catch(err) {
            notifyUser(null, "error", err.response?.data?.error || "Invalid or expired OTP", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    // Handler for OTP input change
    const handleOtpChange = (e, idx) => {
        const val = e.target.value.replace(/[^0-9]/g, "");
        if(!val) return;
        const newArr = [...otpArray];
        newArr[idx] = val[val.length - 1];
        setOtpArray(newArr);
        // Focus next input
        if(idx < 5 && val) {
            otpRefs.current[idx + 1].current.focus();
        }
    };

    // Handler for backspace
    const handleOtpKeyDown = (e, idx) => {
        if(e.key === "Backspace") {
            if(otpArray[idx]) {
                // Just clear current
                const newArr = [...otpArray];
                newArr[idx] = "";
                setOtpArray(newArr);
            } else if(idx > 0) {
                // Move to previous
                otpRefs.current[idx - 1].current.focus();
                const newArr = [...otpArray];
                newArr[idx - 1] = "";
                setOtpArray(newArr);
            }
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center  px-4">
            <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md border border-blue-100">
                <h2 className="text-3xl font-extrabold mb-2 text-center text-navy tracking-tight">
                    RECOVER USERNAME
                </h2>
                <p className="text-center text-gray-500 mb-6 text-sm">
                    We’ll send your username to your registered email address.
                </p>

                {step === 1 && (
                    <>
                        <label className="block mb-2 font-semibold text-navy">
                            Email Address
                        </label>
                        <div className="relative mb-4">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <FiMail />
                            </span>
                            <input
                                type="email"
                                className="w-full border border-blue-200 focus:border-navy focus:ring-2 focus:ring-navy/20 px-10 py-2 rounded-lg transition outline-none"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                autoFocus
                            />
                        </div>
                        <button
                            onClick={handleRequestOtp}
                            className={`w-full bg-navy text-white py-2 rounded-lg font-semibold shadow hover:bg-[#063d5d] transition disabled:opacity-60 disabled:cursor-not-allowed mb-2`}
                            disabled={loading}
                        >
                            {loading ? "Sending OTP..." : "Send OTP"}
                        </button>
                    </>
                )}

                {step === 2 && (
                    <>
                        <label className="block mb-2 font-semibold text-navy">
                            OTP Verification
                        </label>
                        <div className="flex justify-center gap-2 mb-4">
                            {otpArray.map((digit, idx) => (
                                <input
                                    key={idx}
                                    ref={otpRefs.current[idx]}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    className="w-12 h-14 text-center text-2xl border border-blue-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-navy/20 transition outline-none bg-gray-50"
                                    value={digit}
                                    onChange={(e) => handleOtpChange(e, idx)}
                                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                    autoFocus={idx === 0}
                                />
                            ))}
                        </div>
                        <button
                            onClick={handleVerifyOtp}
                            className={`w-full bg-navy text-white py-2 rounded-lg font-semibold shadow hover:bg-[#063d5d] transition disabled:opacity-60 disabled:cursor-not-allowed mb-2`}
                            disabled={loading}
                        >
                            {loading ? "Verifying..." : "Verify OTP"}
                        </button>
                        <p className="text-xs text-gray-400 text-center mt-2">
                            Didn’t receive the OTP?{" "}
                            <span
                                className="text-navy cursor-pointer underline"
                                onClick={handleRequestOtp}
                            >
                                Resend
                            </span>
                        </p>
                    </>
                )}

                {step === 3 && (
                    <div className="flex flex-col items-center justify-center gap-2 py-8">
                        <span
                            className="inline-flex items-center justify-center rounded-full border-4"
                            style={{
                                borderColor: "var(--navy)",
                                width: 64,
                                height: 64,
                            }}
                        >
                            <FiCheckCircle
                                className="text-5xl"
                                style={{ color: "var(--navy)" }}
                            />
                        </span>
                        <div className="text-green-700 font-bold text-lg">
                            Your username has been sent to your email.
                        </div>
                        <div className="text-gray-500 text-sm">
                            Redirecting to login...
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ForgotUsername;
